import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Spinner } from 'react-bootstrap';
import {
  OperatorSpraySessionCombo,
  ComboRegenStatus,
} from '../../services/sprayService';

export interface ComboInstructionCellProps {
  combo: Pick<OperatorSpraySessionCombo, '_id' | 'status' | 'regenStatus' | 'instructionId'>;
  parentStatus: 'draft' | 'planned' | 'in_progress' | 'completed' | 'incomplete' | 'cancelled' | 'signed_off' | 'deleted';
  onView: (comboId: string) => void;
  onRegenerate: (comboId: string) => void;
  onDelete: (comboId: string) => void;
}

const TERMINAL_COMBO = new Set(['completed', 'incomplete', 'cancelled']);
const TERMINAL_PARENT = new Set(['cancelled', 'signed_off', 'deleted']);

function isLocked(comboStatus: string, parentStatus: string): boolean {
  return TERMINAL_COMBO.has(comboStatus) || TERMINAL_PARENT.has(parentStatus);
}

const ComboInstructionCell: React.FC<ComboInstructionCellProps> = ({
  combo, parentStatus, onView, onRegenerate, onDelete,
}) => {
  const { t } = useTranslation();
  const regenStatus: ComboRegenStatus = combo.regenStatus ?? 'none';
  const locked = isLocked(combo.status, parentStatus);
  const hasPdf = !!combo.instructionId;

  const handleDelete = () => {
    if (window.confirm(t('spray.combo.pdf.deleteConfirm'))) {
      onDelete(combo._id);
    }
  };

  if (regenStatus === 'regenerating') {
    return (
      <div className="d-flex align-items-center gap-2" data-testid="combo-instruction-cell">
        <Spinner
          animation="border"
          size="sm"
          aria-label={t('spray.combo.pdf.regenerating')}
          title={t('spray.combo.pdf.regenerating')}
        />
      </div>
    );
  }

  if (regenStatus === 'failed') {
    return (
      <div className="d-flex align-items-center gap-2" data-testid="combo-instruction-cell">
        <Badge
          bg="danger"
          title={t('spray.combo.pdf.regenFailed')}
        >
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
        </Badge>
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          onClick={() => onRegenerate(combo._id)}
          disabled={locked}
          aria-label={t('spray.combo.pdf.regenerate')}
          title={t('spray.combo.pdf.regenerate')}
        >
          <i className="bi bi-arrow-clockwise" aria-hidden="true" />
          <span className="visually-hidden">{t('spray.combo.pdf.regenerate')}</span>
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--danger"
          onClick={handleDelete}
          disabled={locked}
          aria-label={t('spray.combo.pdf.delete')}
          title={t('spray.combo.pdf.delete')}
        >
          <i className="bi bi-trash" aria-hidden="true" />
          <span className="visually-hidden">{t('spray.combo.pdf.delete')}</span>
        </button>
      </div>
    );
  }

  if (!hasPdf) {
    return (
      <div className="d-flex align-items-center gap-2" data-testid="combo-instruction-cell">
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          onClick={() => onRegenerate(combo._id)}
          disabled={locked}
          aria-label={t('spray.combo.pdf.generate')}
          title={t('spray.combo.pdf.generate')}
        >
          <i className="bi bi-file-earmark-pdf-fill" aria-hidden="true" />
          <span className="visually-hidden">{t('spray.combo.pdf.generate')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2" data-testid="combo-instruction-cell">
      <button
        type="button"
        className="pat-btn pat-btn--subtle"
        onClick={() => onView(combo._id)}
        aria-label={t('spray.combo.pdf.viewPdf')}
        title={t('spray.combo.pdf.viewPdf')}
      >
        <i className="bi bi-eye" aria-hidden="true" />
        <span className="visually-hidden">{t('spray.combo.pdf.viewPdf')}</span>
      </button>
      <button
        type="button"
        className="pat-btn pat-btn--outline"
        onClick={() => onRegenerate(combo._id)}
        disabled={locked}
        aria-label={t('spray.combo.pdf.regenerate')}
        title={locked ? t('spray.combo.pdf.locked') : t('spray.combo.pdf.regenerate')}
      >
        <i className="bi bi-arrow-clockwise" aria-hidden="true" />
        <span className="visually-hidden">{t('spray.combo.pdf.regenerate')}</span>
      </button>
      <button
        type="button"
        className="pat-btn pat-btn--danger"
        onClick={handleDelete}
        disabled={locked}
        aria-label={t('spray.combo.pdf.delete')}
        title={locked ? t('spray.combo.pdf.locked') : t('spray.combo.pdf.delete')}
      >
        <i className="bi bi-trash" aria-hidden="true" />
        <span className="visually-hidden">{t('spray.combo.pdf.delete')}</span>
      </button>
    </div>
  );
};

export default ComboInstructionCell;
